import { zodResolver } from '@hookform/resolvers/zod';
import { FloatField } from '@mdvip/shared/components/blocks/FloatField';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { FieldLegend, FieldSet } from '@mdvip/shared/components/ui/field';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { useMemo } from 'react';
import { Controller, type FieldErrors, FormProvider, type Resolver, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { usePracticeInfo } from '@/hooks/practice/usePracticeInfo';
import { SubmitReferralError } from '@/hooks/refer-a-friend/referAFriendApi';
import { useSubmitReferral } from '@/hooks/refer-a-friend/useSubmitReferral';
import { type SubmitReferralFormValues, SubmitReferralSchema } from '@/lib/refer-a-friend/referAFriend.schema';

// Mirrors the private MAX_REFERRAL_RECIPIENTS in referAFriend.schema.ts. Not imported — that
// constant isn't exported, and this file isn't meant to touch the delivered schema.
const MAX_RECIPIENTS = 5;

const EMPTY_RECIPIENT = { name: '', email: '' } as const;

// Same width on every step (form, error, and the three terminal outcomes) so the dialog never
// resizes as it advances — matches OneTimePaymentModal's success screen precedent.
const MODAL_WIDTH_CLASS = 'sm:max-w-2xl';

export interface ReferAFriendModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Validation messages are dynamic i18n keys returned by the zod schema, so bypass the strict
 * typed-t. Same rationale as payments' `useTranslateError` (see oneTimePayment/fields.tsx).
 */
function useTranslateError() {
  const { t } = useTranslation('members');
  return t as unknown as (message: string, values?: Record<string, string>) => string;
}

/**
 * Drops fully-blank recipient rows before they reach the schema, keeping each survivor's original
 * on-screen index alongside it. The design requires only the first friend row; an untouched row
 * 2-5 the member never typed into must not block submission. A *partially* filled row (a name with
 * no email, or vice versa) is a real validation error and is left alone for the schema to catch —
 * "touched" means either field is non-empty.
 *
 * Row 0 is never pruned, blank or not: it's the mandatory row, so a blank submit must surface its
 * own "Name is required."/"Enter a valid email address." field errors, not fall through to the
 * array-level `recipientsMin` message (which is for a body-less API call, not something the UI can
 * ever produce once this holds — there's always at least one row on screen).
 *
 * The original indices are returned because `Array.filter` reindexes survivors: dropping row 1
 * shifts row 2 into slot 1. Without tracking that shift, a real error on row 2 would come back
 * from the schema addressed to "recipients.1" and render under row 1's now-blank field instead.
 */
function pruneBlankRecipients(recipients: SubmitReferralFormValues['recipients']): {
  pruned: SubmitReferralFormValues['recipients'];
  originalIndices: number[];
} {
  const originalIndices: number[] = [];
  const pruned = recipients.filter((recipient, index) => {
    const keep = index === 0 || recipient.name.trim() !== '' || recipient.email.trim() !== '';
    if (keep) originalIndices.push(index);
    return keep;
  });
  return { pruned, originalIndices };
}

/**
 * Wraps `zodResolver` so blank rows are pruned before validation runs, then maps any per-row
 * `recipients.<i>` issue the schema raises back onto the original row it came from — see
 * `pruneBlankRecipients`'s own doc comment for why that remap is necessary. The schema itself
 * stays a pure mirror of the backend contract (see referAFriend.schema.ts's own doc comment) and
 * has no notion of which rows the member has "touched" in the UI — that's this form's concern, not
 * the schema's.
 */
const referAFriendResolver: Resolver<SubmitReferralFormValues> = async (values, context, options) => {
  const { pruned, originalIndices } = pruneBlankRecipients(values.recipients);
  const result = await (zodResolver(SubmitReferralSchema) as unknown as Resolver<SubmitReferralFormValues>)(
    { ...values, recipients: pruned },
    context,
    options,
  );

  const recipientsErrors = result.errors.recipients as Record<string, unknown> | undefined;
  if (recipientsErrors) {
    const remapped: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(recipientsErrors)) {
      // A numeric key is a per-row issue addressed to the *pruned* array; 'root'/'message'/'type'
      // are the array-level issue's own keys (see countFieldErrors's doc comment for both shapes)
      // and aren't a row index at all, so they pass through unchanged.
      const originalIndex = /^\d+$/.test(key) ? originalIndices[Number(key)] : key;
      remapped[originalIndex] = value;
    }
    (result.errors as { recipients?: unknown }).recipients = remapped;
  }

  return result;
};

/**
 * Counts leaf field errors for the "N fields need attention" summary — matches the Claude Design
 * handoff's own `errorCount`. RHF's `errors.recipients` isn't a real array: it's either a plain
 * `{ message, type }` (only the array-level issue) or `{ [index]: { name?, email? }, root? }`
 * (per-row errors, optionally alongside the array-level one) — verified empirically, not assumed
 * (see the resolver's own doc comment). This walks both shapes rather than trusting `Array.isArray`.
 */
function countFieldErrors(errors: FieldErrors<SubmitReferralFormValues>): number {
  let count = 0;
  if (errors.referrerName) count += 1;
  if (errors.referrerEmail) count += 1;

  const recipients = errors.recipients as Record<string, { name?: unknown; email?: unknown }> | undefined;
  if (recipients) {
    for (const [key, value] of Object.entries(recipients)) {
      if (key === 'root') {
        if (value) count += 1;
      } else if (key === 'message') {
        // The array-level-only shape puts `message`/`type` directly on `errors.recipients`.
        count += 1;
      } else if (key !== 'type') {
        if (value?.name) count += 1;
        if (value?.email) count += 1;
      }
    }
  }
  return count;
}

/**
 * ReferAFriendModal — invite up to 5 friends to learn about the member's own physician.
 *
 * Own name/email are prefilled from `useAuth()` and editable; the physician's name (from
 * `usePracticeInfo()`) is cosmetic intro copy only — it renders a generic fallback rather than
 * blocking if that query is slow or errors, since it's never sent as mutation input (the backend
 * resolves the physician itself). The intro quote's copy is carried over verbatim from the Claude
 * Design handoff (itself carried over verbatim from the legacy portal modal).
 *
 * Layout follows the design's two-column treatment above the `@3xl` container breakpoint (a
 * context rail — the physician's own quote plus the privacy notice — beside the form) and stacks
 * the rail above the form below it. The `@container` query is keyed to the modal's own width, not
 * the viewport, matching the codebase's established pattern (see PaymentCenterPage.tsx).
 *
 * State lives for as long as this component is mounted, matching `OneTimePaymentModal`: the
 * caller remounts it (via a `key`) to start a fresh flow, rather than this component resetting
 * itself on `open` transitions.
 */
export function ReferAFriendModal({ open, onOpenChange }: ReferAFriendModalProps) {
  const { t } = useTranslation('members');
  const translateError = useTranslateError();
  const { user } = useAuth();
  // Navigation mounts this modal permanently (see its own doc comment on remounting via `key`),
  // so this fetch stays gated behind `open` — otherwise every authenticated page load would fire
  // it for members who never open the modal.
  const { data: practice } = usePracticeInfo({ enabled: open });
  const { submit, isPending, data, error } = useSubmitReferral();

  const defaultValues = useMemo<SubmitReferralFormValues>(
    () => ({
      referrerName: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
      referrerEmail: user?.email ?? '',
      recipients: [{ ...EMPTY_RECIPIENT }],
    }),
    [user?.firstName, user?.lastName, user?.email],
  );

  const form = useForm<SubmitReferralFormValues>({
    resolver: referAFriendResolver,
    mode: 'onTouched',
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'recipients' });

  // An array-level issue (recipientsMin/Max/DuplicateEmail — attached to path ['recipients'], not
  // a row) lands at `errors.recipients.root` when per-row errors also exist, or directly on
  // `errors.recipients` when it's the only issue — verified against the installed
  // @hookform/resolvers/zod, not assumed.
  const recipientsError = form.formState.errors.recipients;
  const recipientsRootMessage = recipientsError?.root?.message ?? (recipientsError as { message?: string })?.message;
  const errorCount = countFieldErrors(form.formState.errors);
  // `useFieldArray`'s append/remove re-run the resolver against the whole form immediately,
  // regardless of `mode: 'onTouched'` — so adding a still-blank second row would otherwise trip
  // `recipientsMin` (pruned to zero recipients) before the member has done anything wrong. Gating
  // the array-level message and summary banner behind a real submit attempt keeps per-field
  // onBlur feedback (which isn't affected by this) working exactly as before.
  const hasAttemptedSubmit = form.formState.isSubmitted;

  const handleSubmit = form.handleSubmit((values) => {
    submit(values);
  });

  const atCap = fields.length >= MAX_RECIPIENTS;

  // `error` is only ever the mutation's own Failure variant (SubmitReferralError, currently
  // PHYSICIAN_UNAVAILABLE) or an infrastructure error (extractGqlErrorCode) — never both at once,
  // since `useSubmitReferral` throws on the Failure variant instead of resolving it as data.
  const errorCode = error
    ? error instanceof SubmitReferralError
      ? error.code
      : extractGqlErrorCode(error)
    : undefined;

  const step: 'form' | 'success' | 'partial' | 'physicianUnavailable' | 'error' = data
    ? data.failed.length > 0
      ? 'partial'
      : 'success'
    : errorCode === 'PHYSICIAN_UNAVAILABLE'
      ? 'physicianUnavailable'
      : errorCode
        ? 'error'
        : 'form';

  // sentCount + failed.length, not a separate stored total — this is the whole population the
  // member tried to reach. Only meaningful when step === 'partial'.
  const partialTotalCount = (data?.sentCount ?? 0) + (data?.failed.length ?? 0);

  // TOO_MANY_REQUESTS gets its own description under the same shell; every other infra code
  // (UNKNOWN, NETWORK_ERROR, ...) gets the generic "didn't reach our servers" copy — no code falls
  // through without a message, since `errorCode` is only reached once `error` is actually set.
  const errorDescription =
    errorCode === 'TOO_MANY_REQUESTS'
      ? t('referAFriend.error.tooManyRequestsDescription')
      : t('referAFriend.error.description');

  // Resubmitting can't change a clean/partial send (it already happened) or fix a missing
  // physician record — only the 'error' step's "Try again" is a real retry.
  const sendDisabled = isPending || step === 'success' || step === 'partial' || step === 'physicianUnavailable';

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange} className={MODAL_WIDTH_CLASS} showCloseButton={!isPending}>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('referAFriend.modalTitle')}</ResponsiveModalTitle>
        <p className="mt-2 text-sm text-muted-foreground">
          {practice?.name ? t('referAFriend.intro', { physician: practice.name }) : t('referAFriend.introFallback')}
        </p>
      </ResponsiveModalHeader>

      <ResponsiveModalBody className="p-0">
        <FormProvider {...form}>
          {/* @container is keyed to the modal's own width, not the viewport — same pattern as
              PaymentCenterPage's summary grid. Has to live on its own wrapper, separate from the
              grid that reads @3xl:, since a container query can't resolve against the element it's
              declared on. */}
          <div className="@container">
            <div className="grid grid-cols-1 gap-8 p-4 @3xl:grid-cols-[16rem_1fr] @3xl:gap-10 sm:p-8">
              {/* Context rail: the physician's own referral quote plus the privacy notice. Sticky
                  above @3xl so it stays visible while the form scrolls; stacks above the form
                  below it. Copy carried over verbatim from the Claude Design handoff (itself
                  carried over verbatim from the legacy portal modal) — but not its literal
                  classes: the handoff used text-foreground/80 and bg-muted/60, which predate this
                  branch's own color-ladder audit (docs/070-styling.md) reserving alpha for photo
                  scrims and decorative washes, not text/surfaces. Opaque tokens here instead. */}
              <aside className="flex flex-col gap-6 @3xl:sticky @3xl:top-0 @3xl:self-start">
                <figure className="m-0 flex flex-col gap-3 border-l-2 border-primary pl-4">
                  <blockquote className="text-sm leading-relaxed text-muted-foreground">
                    {t('referAFriend.introQuote.quote')}
                  </blockquote>
                  <figcaption className="text-xs font-medium text-muted-foreground">
                    {practice?.name ?? t('referAFriend.introQuote.citationFallback')}
                  </figcaption>
                </figure>
                <div className="flex flex-col gap-1 rounded-lg bg-muted p-4">
                  <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                    <Icon name="lock" weight="solid" size="xs" />
                    {t('referAFriend.privacyNotice.title')}
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {t('referAFriend.privacyNotice.body')}
                  </p>
                </div>
              </aside>

              <div className="flex min-w-0 flex-col gap-6">
                {/* Outcome banners render inline, above the still-visible form — matching the
                    design's own layering (sent/serverError/errorCount Alerts stacked above "Your
                    Details" in the same modal) rather than replacing the modal's content. */}
                {step === 'success' && (
                  <Alert variant="success">
                    <Icon name="circleCheck" weight="solid" />
                    <AlertTitle>{t('referAFriend.success.title')}</AlertTitle>
                    <AlertDescription>
                      <p>
                        {practice?.name
                          ? t('referAFriend.success.descriptionWithPhysician', { physician: practice.name })
                          : t('referAFriend.success.descriptionFallback')}
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
                {step === 'partial' && (
                  <Alert variant="warning">
                    <Icon name="alertTriangle" weight="solid" />
                    <AlertTitle>{t('referAFriend.partial.title')}</AlertTitle>
                    <AlertDescription>
                      <p>
                        {t('referAFriend.partial.description', {
                          sentCount: String(data?.sentCount ?? 0),
                          totalCount: String(partialTotalCount),
                        })}
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
                {step === 'physicianUnavailable' && (
                  <Alert variant="destructive">
                    <Icon name="circleXmark" weight="solid" />
                    <AlertTitle>{t('referAFriend.physicianUnavailable.title')}</AlertTitle>
                    <AlertDescription>
                      <p>
                        {t('referAFriend.physicianUnavailable.description', {
                          phone: MDVIP_COMPANY_INFO.memberSupportPhone,
                        })}
                      </p>
                    </AlertDescription>
                  </Alert>
                )}
                {step === 'error' && (
                  <Alert variant="destructive">
                    <Icon name="circleXmark" weight="solid" />
                    <AlertTitle>{t('referAFriend.error.title')}</AlertTitle>
                    <AlertDescription>
                      <p>{errorDescription}</p>
                    </AlertDescription>
                  </Alert>
                )}
                {step === 'form' && hasAttemptedSubmit && errorCount > 0 && (
                  <Alert variant="destructive">
                    <Icon name="alertCircle" weight="solid" />
                    <AlertTitle>{t('referAFriend.validationSummary.title', { count: errorCount })}</AlertTitle>
                    <AlertDescription>
                      <p>{t('referAFriend.validationSummary.description')}</p>
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <FieldLegend variant="label" className="mb-3">
                    {t('referAFriend.yourDetails.sectionLabel')}
                  </FieldLegend>
                  <div className="grid grid-cols-1 gap-4">
                    <Controller
                      control={form.control}
                      name="referrerName"
                      render={({ field, fieldState }) => (
                        <FloatField
                          ref={field.ref}
                          id="referrerName"
                          label={t('referAFriend.yourDetails.nameLabel')}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={(event) => field.onChange(event.target.value)}
                          error={fieldState.invalid}
                          disabled={isPending}
                          hint={fieldState.error?.message ? translateError(fieldState.error.message) : undefined}
                        />
                      )}
                    />
                    <Controller
                      control={form.control}
                      name="referrerEmail"
                      render={({ field, fieldState }) => (
                        <FloatField
                          ref={field.ref}
                          id="referrerEmail"
                          label={t('referAFriend.yourDetails.emailLabel')}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={(event) => field.onChange(event.target.value)}
                          error={fieldState.invalid}
                          disabled={isPending}
                          hint={fieldState.error?.message ? translateError(fieldState.error.message) : undefined}
                          inputProps={{ type: 'email', inputMode: 'email' }}
                        />
                      )}
                    />
                  </div>
                </div>

                <FieldSet className="gap-3">
                  <div className="mb-3 flex items-baseline gap-2">
                    <FieldLegend variant="label" className="mb-0">
                      {t('referAFriend.friends.sectionLabel')}
                    </FieldLegend>
                    <span className="text-xs text-muted-foreground">
                      {t('referAFriend.friends.countLabel', { count: fields.length, max: MAX_RECIPIENTS })}
                    </span>
                  </div>

                  {fields.map((field, index) => (
                    <div key={field.id} className="flex items-start gap-3 rounded-lg border bg-card p-3">
                      <span
                        aria-hidden="true"
                        className="mt-2.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
                      >
                        {index + 1}
                      </span>
                      <div className="grid min-w-0 flex-1 grid-cols-1 gap-4">
                        <Controller
                          control={form.control}
                          name={`recipients.${index}.name`}
                          render={({ field: nameField, fieldState }) => (
                            <FloatField
                              ref={nameField.ref}
                              id={`recipient-${index}-name`}
                              label={t(
                                index === 0
                                  ? 'referAFriend.friends.nameLabelRequired'
                                  : 'referAFriend.friends.nameLabel',
                              )}
                              value={nameField.value}
                              onBlur={nameField.onBlur}
                              onChange={(event) => nameField.onChange(event.target.value)}
                              error={fieldState.invalid}
                              disabled={isPending}
                              hint={fieldState.error?.message ? translateError(fieldState.error.message) : undefined}
                            />
                          )}
                        />
                        <Controller
                          control={form.control}
                          name={`recipients.${index}.email`}
                          render={({ field: emailField, fieldState }) => (
                            <FloatField
                              ref={emailField.ref}
                              id={`recipient-${index}-email`}
                              label={t(
                                index === 0
                                  ? 'referAFriend.friends.emailLabelRequired'
                                  : 'referAFriend.friends.emailLabel',
                              )}
                              value={emailField.value}
                              onBlur={emailField.onBlur}
                              onChange={(event) => emailField.onChange(event.target.value)}
                              error={fieldState.invalid}
                              disabled={isPending}
                              hint={fieldState.error?.message ? translateError(fieldState.error.message) : undefined}
                              inputProps={{ type: 'email', inputMode: 'email' }}
                            />
                          )}
                        />
                      </div>
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={isPending}
                          aria-label={t('referAFriend.friends.removeAriaLabel', { index: String(index + 1) })}
                          onClick={() => remove(index)}
                        >
                          <Icon name="trash" />
                        </Button>
                      )}
                    </div>
                  ))}

                  {hasAttemptedSubmit && recipientsRootMessage && (
                    <p className="flex items-start gap-1.5 text-xs text-destructive-foreground">
                      <Icon name="alertCircle" weight="solid" size="xs" className="mt-px shrink-0" />
                      {translateError(recipientsRootMessage)}
                    </p>
                  )}

                  {atCap ? (
                    <p className="text-xs text-muted-foreground">{t('referAFriend.friends.maxHint')}</p>
                  ) : (
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() => append({ ...EMPTY_RECIPIENT })}
                      className="mt-1 flex h-6 items-center text-sm font-medium text-foreground underline decoration-1 underline-offset-2 disabled:pointer-events-none disabled:opacity-40"
                    >
                      {t('referAFriend.friends.addAnother')}
                    </button>
                  )}
                </FieldSet>
              </div>
            </div>
          </div>
        </FormProvider>
      </ResponsiveModalBody>

      <ResponsiveModalFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
          {t('referAFriend.actions.cancel')}
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={sendDisabled}>
          {isPending ? (
            <>
              <Icon name="spinner" weight="solid" size="sm" className="animate-spin" />
              {t('referAFriend.actions.sending')}
            </>
          ) : step === 'error' ? (
            t('referAFriend.error.tryAgain')
          ) : (
            <>
              <Icon name="paperPlane" size="sm" />
              {t('referAFriend.actions.send')}
            </>
          )}
        </Button>
      </ResponsiveModalFooter>
    </ResponsiveModal>
  );
}
